import React from 'react';
import classes from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../../contexts/kato.context';
import Card from '../../../../components/card/card.component';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  BarChart,
  Bar,
} from 'recharts';
import { EmploymentCategories } from '../../../../types/screening.types';
import { useUser } from '../../../../contexts/user.context';

const baseClass = 'human-resources-tab';
const translationKey = 'screening.human-resources';

const HumanResourcesTab = () => {
  const { t } = useTranslation();
  const { isModulePresent } = useUser();

  const { humanResources } = useKato().screeningInfo;
  const {
    populationDynamic,
    populationByAge,
    populationBySocialStatus,
    employment,
  } = humanResources || {};

  const barData = populationBySocialStatus
    ? [
        {
          name: t(`${translationKey}.tsa-recipients`),
          value: Number(populationBySocialStatus.TSArecipients),
        },
        {
          name: t(`${translationKey}.pensioners`),
          value: Number(populationBySocialStatus.pensioners),
        },
        {
          name: t(`${translationKey}.large-families`),
          value: Number(populationBySocialStatus.largeFamilies),
        },
        {
          name: t(`${translationKey}.disabled`),
          firstGroupDisabled: Number(
            populationBySocialStatus.firstGroupDisabled,
          ),
          secondGroupDisabled: Number(
            populationBySocialStatus.secondGroupDisabled,
          ),
          thirdGroupDisabled: Number(
            populationBySocialStatus.thirdGroupDisabled,
          ),
        },
      ]
    : [];

  const employmentData = employment
    ? [
        {
          name: t(`${translationKey}.hired`),
          budget: employment[EmploymentCategories.hired_budget],
          private: employment[EmploymentCategories.hired_private],
        },
        {
          name: t(`${translationKey}.self-employed`),
          budget: employment[EmploymentCategories.selfEmployed],
        },
        {
          name: t(`${translationKey}.unemployed`),
          budget: employment[EmploymentCategories.unemployed],
        },
        {
          name: t(`${translationKey}.working-elderly`),
          budget: employment[EmploymentCategories.workingElderly],
        },
      ]
    : [];

  if (!humanResources) return null;
  return (
    <div className={classes[baseClass]}>
      <div className={classes[`${baseClass}__grid`]}>
        {populationDynamic &&
          isModulePresent('human-resources-population-dynamic') && (
            <Card title={t(`${translationKey}.population-dynamic`)}>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={populationDynamic}
                  margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                >
                  <CartesianGrid vertical={false} horizontal={true} />
                  <XAxis dataKey="year" tick={false} axisLine={false} />
                  <YAxis hide />
                  <Line
                    type="monotone"
                    dataKey="population"
                    stroke="#6968FD"
                    dot={{ stroke: '#6968FD', fill: '#6968FD', r: 4 }}
                  >
                    <LabelList
                      dataKey="year"
                      position="top"
                      fill="#818689"
                      fontSize={'0.75rem'}
                    />
                    <LabelList
                      dataKey="population"
                      position="bottom"
                      fill="#202224"
                      formatter={(val) => val?.toLocaleString()}
                      fontSize={'0.75rem'}
                    />
                  </Line>
                  <Tooltip
                    cursor={{ fill: 'transparent' }} // отключить серый фон
                    formatter={(value: number) => [
                      value.toLocaleString('ru-RU'),
                      t('screening.human-resources.population'), // ключ перевода
                    ]}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}
        {populationByAge && isModulePresent('population-by-age') && (
          <Card title={t(`${translationKey}.population-by-age`)}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={populationByAge}
                barCategoryGap={0}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              >
                <CartesianGrid vertical={false} horizontal={true} />
                <XAxis dataKey="age" axisLine={false} tickLine={false} />
                <YAxis hide />
                <Bar
                  dataKey="population"
                  fill="#118F84"
                  barSize={76}
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    dataKey="population"
                    position="insideTop"
                    style={{ fill: 'white', fontWeight: 600 }}
                    formatter={(value) => value?.toLocaleString()}
                  />
                </Bar>
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  formatter={(value: number) => [
                    value.toLocaleString('ru-RU'),
                    t('screening.human-resources.population'),
                  ]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {populationBySocialStatus &&
          isModulePresent('population-by-social-status') && (
            <Card title={t(`${translationKey}.population-by-social-status`)}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={barData}
                  barGap={-60}
                  barCategoryGap={32}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                >
                  <CartesianGrid vertical={false} horizontal={true} />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fontSize: '0.625rem',
                      fill: '#818689',
                    }}
                  />
                  <YAxis hide />
                  <Bar
                    dataKey="value"
                    fill="#118F84"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                    minPointSize={(value: any) => (value > 0 ? 20 : 0)}
                  >
                    <LabelList
                      dataKey="value"
                      position="insideTop"
                      style={{ fill: 'white', fontWeight: 600 }}
                      formatter={(val) =>
                        val?.toString() !== '0' ? val?.toLocaleString() : ''
                      }
                    />
                  </Bar>

                  <Bar
                    dataKey="thirdGroupDisabled"
                    stackId="disabled"
                    fill="#5BC7D9"
                    isAnimationActive={false}
                  >
                    <LabelList
                      position="insideTop"
                      dataKey="thirdGroupDisabled"
                      style={{ fill: 'white', fontWeight: 600 }}
                      formatter={(val) => (val ? val?.toLocaleString() : '')}
                    />
                  </Bar>
                  <Bar
                    dataKey="secondGroupDisabled"
                    stackId="disabled"
                    fill="#92D050"
                  >
                    <LabelList
                      position="insideTop"
                      dataKey="secondGroupDisabled"
                      style={{ fill: 'white', fontWeight: 600 }}
                      formatter={(val) => (val ? val?.toLocaleString() : '')}
                    />
                  </Bar>
                  <Bar
                    dataKey="firstGroupDisabled"
                    stackId="disabled"
                    fill="#2F6DBD"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                    minPointSize={(val: any) => (val > 0 ? 20 : 0)}
                  >
                    <LabelList
                      position="insideTop"
                      dataKey="firstGroupDisabled"
                      style={{ fill: 'white', fontWeight: 600 }}
                      formatter={(val) => (val ? val?.toLocaleString() : '')}
                    />
                  </Bar>

                  <Tooltip
                    cursor={{ fill: 'transparent' }}
                    formatter={(value: number, key: string) => {
                      const translated = t(`screening.human-resources.${key}`);
                      return [value.toLocaleString('ru-RU'), translated];
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        {employment && isModulePresent('human-resources-employment') && (
          <Card title={t(`${translationKey}.employment`)}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={employmentData}
                barGap={-60}
                barCategoryGap={32}
                margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              >
                <CartesianGrid vertical={false} horizontal={true} />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: '0.625rem',
                    fill: '#818689',
                  }}
                />
                <YAxis hide />
                {/* Stacked Bar для disabled */}
                <Bar
                  dataKey="budget"
                  stackId="hired"
                  fill="#118F84"
                  isAnimationActive={false}
                  minPointSize={20}
                >
                  <LabelList
                    dataKey="budget"
                    position="insideTop"
                    style={{ fill: 'white', fontWeight: 600 }}
                    formatter={(val) => (val ? val?.toLocaleString() : '')}
                  />
                </Bar>
                <Bar
                  dataKey="private"
                  stackId="hired"
                  fill="#92D050"
                  isAnimationActive={false}
                  radius={[4, 4, 0, 0]}
                >
                  <LabelList
                    dataKey="private"
                    position="insideTop"
                    style={{ fill: 'white', fontWeight: 600 }}
                    formatter={(val) => (val ? val?.toLocaleString() : '')}
                  />
                </Bar>
                <Bar
                  dataKey="value"
                  fill="#118F84"
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={false}
                >
                  <LabelList
                    position="insideTop"
                    style={{ fill: 'white', fontWeight: 600 }}
                    formatter={(val) => (val ? val?.toLocaleString() : '')}
                  />
                </Bar>
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  formatter={(value: number, key: string) => {
                    const translated = t(`${translationKey}.${key}`);
                    return [value.toLocaleString('ru-RU'), translated];
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
};

export default HumanResourcesTab;
